import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { ApiError } from '../api/client'
import { patchEndDate, type TitleCard } from './api'
import { detailsErrorText } from './details'
import { addMonths, canSave, deltaText, fmtDate, monthsBetween, REASONS, restores, setToMonth, startFrom, todayIso } from './endDate'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const inp = `min-h-[40px] rounded-lg border border-ink-400 bg-ink-800 px-3 text-paper ${focus}`
const lbl = 'flex flex-col gap-1.5 text-[13px] font-semibold text-paper/70'
const btn = `inline-flex min-h-[40px] items-center rounded-lg border border-ink-400 px-4 text-[13px] font-semibold text-paper hover:bg-ink-600 ${focus}`
const chip = `inline-flex min-h-[36px] items-center rounded-full border px-3 text-[13px] font-semibold ${focus}`
const CHIPS = [1, 2, 3, 6, 12]

export function EndDate({ memberId, card, canEmail, onSaved, onCancel }: {
  memberId: number
  card: TitleCard
  canEmail: boolean
  onSaved: () => void
  onCancel: () => void
}) {
  // Count from the old end, or from today when it has passed (a lapsed
  // membership) or there is none (open-ended).
  const today = todayIso()
  const base = startFrom(card.end_date, today)
  const [newEnd, setNewEnd] = useState(() => addMonths(base, 1))
  const [reason, setReason] = useState('')
  const [note, setNote] = useState('')
  const [email, setEmail] = useState(canEmail)
  const [error, setError] = useState<string | null>(null)
  const delta = monthsBetween(base, newEnd)

  const save = useMutation({
    mutationFn: () => patchEndDate(memberId, card.subscription_id as number,
      { new_end: newEnd, reason, note: note.trim(), email_member: canEmail && email }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiError ? detailsErrorText(e.status, e.detail) : 'Could not save. Check your connection and try again.'),
  })
  const ready = canSave({ reason, note }) && newEnd !== card.end_date && card.subscription_id !== null

  return (
    <form
      aria-labelledby={`end-h-${card.slug}`}
      className="flex flex-col gap-4 rounded-xl border border-ink-500 bg-ink-700 p-5"
      onSubmit={(e) => { e.preventDefault(); setError(null); if (ready) save.mutate() }}
    >
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <h2 id={`end-h-${card.slug}`} className="font-serif text-[20px]">Change end date · {card.name}</h2>
        <button type="button" className={btn} onClick={onCancel}>Close</button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 text-[13px] font-semibold text-paper/70">Add</span>
        {CHIPS.map((n) => {
          const on = delta === n
          return (
            <button
              key={n}
              type="button"
              aria-pressed={on}
              onClick={() => setNewEnd(addMonths(base, n))}
              className={`${chip} ${on ? 'border-amber-glow bg-amber-glow/15 text-paper' : 'border-ink-400 text-paper/70 hover:bg-ink-600'}`}
            >
              +{n} {n === 1 ? 'month' : 'months'}
            </button>
          )
        })}
        <span className="mx-1.5 text-paper/60">or</span>
        <label className="inline-flex items-center gap-2 text-[13px] font-semibold text-paper/70">
          Set to
          <input
            className={`${inp} w-[170px]`}
            type="month"
            value={newEnd.slice(0, 7)}
            onChange={(e) => { if (/^\d{4}-\d{2}$/.test(e.target.value)) setNewEnd(setToMonth(base, e.target.value)) }}
          />
        </label>
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3.5">
        <label className={lbl}>
          Reason (required)
          <select className={inp} value={reason} onChange={(e) => setReason(e.target.value)} required>
            <option value="">Choose a reason</option>
            {REASONS.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </label>
        <label className={lbl}>
          Note for the record{reason === 'other' ? ' (required)' : ''}
          <textarea className={`${inp} py-2`} rows={2} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Two issues lost in the post, Aug and Sep" />
        </label>
      </div>
      <div className="flex flex-wrap items-baseline gap-3 rounded-lg bg-ink-800 px-4 py-3" aria-live="polite">
        <span className="font-mono">{card.end_date ? fmtDate(card.end_date) : 'No end date'}</span>
        <span aria-hidden="true">→</span>
        <span className="sr-only">changes to</span>
        <span className="font-mono text-amber-glow">{fmtDate(newEnd)}</span>
        <span className="font-medium text-paper/70">{deltaText(delta)}{base !== card.end_date && delta !== 0 ? ' from today' : ''}</span>
        {restores({ status: card.status, newEnd, today }) && (
          <span className="font-semibold text-green-500">This will restore the membership.</span>
        )}
      </div>
      <div className="flex flex-wrap gap-4 text-paper/70">
        {canEmail ? (
          <label className="inline-flex min-h-[32px] items-center gap-2">
            <input type="checkbox" checked={email} onChange={(e) => setEmail(e.target.checked)} />
            Email the new end date to the member
          </label>
        ) : (
          <span className="self-center">No email goes out: the member has no address we can write to.</span>
        )}
        <span className="self-center text-[13px] text-paper/60">No money changes hands. The renewal notice moves with the new date.</span>
      </div>
      {error && <p role="alert" className="m-0 text-red-500">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={!ready || save.isPending} className={`inline-flex min-h-[40px] items-center rounded-lg bg-paper px-4 font-bold text-ink-900 disabled:opacity-50 ${focus}`}>
          {save.isPending ? 'Saving…' : 'Save new end date'}
        </button>
        <button type="button" className={btn} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}
